/**
 * Pure helpers for Megaventory payment-terms enumeration ↔ display label.
 * Kept in sync with `src/config/megaventory-payment-terms-helpers.ts` (server); this
 * copy lives under `ui/` so the production Docker image build (Vite only sees `ui/`)
 * succeeds. Change both files together.
 *
 * The integrator loads labels from `public.megaventory_payment_terms_label`; callers
 * pass the current `enumeration → label` map (from cache or an API response).
 *
 * `FALLBACK_*` lives here (not in the DB cache module) so the Vite UI can import it
 * without pulling Node-only dependencies such as `pino`.
 */

/** Seed map when the table is empty, offline, or before the first API response. Keep in sync with migrations seeding `public.megaventory_payment_terms_label`. */
export const FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS: Record<string, string> = {
  CustomPaymentTerms1: "Payment In Advance",
  CustomPaymentTerms2: "Net45",
  None: "Payment In Advance",
  Net10: "Net 10",
  Net15: "Net 15",
  Net30: "Net 30",
  Net60: "Net 180",
  Net90: "Net 90",
  DueOnReceipt: "Due on receipt",
  OtherPaymentTerms: "Other payment terms",
  Net45: "Net 45",
  Net180: "Net 180",
  PaymentInAdvance: "Payment in advance",
  Other: "Other payment terms",
  "T/T": "T/T",
};

export function mapMegaventoryPaymentTermsToLabel(
  paymentTerms: unknown,
  enumerationToLabel: Record<string, string>,
): string | undefined {
  if (paymentTerms === null || paymentTerms === undefined) {
    return undefined;
  }
  const raw = String(paymentTerms).trim();
  if (raw === "") {
    return undefined;
  }
  return enumerationToLabel[raw] ?? raw;
}

/**
 * Turn UI text (friendly label or MV enumeration key) into the MV enumeration
 * string for PurchaseOrderPaymentTermsEnumeration. Unknown values pass through.
 */
export function megaventoryEnumerationFromPaymentTermsUserInput(
  input: string | null | undefined,
  enumerationToLabel: Record<string, string>,
): string {
  if (input === null || input === undefined) {
    return "None";
  }
  const t = String(input).trim();
  if (t === "") {
    return "None";
  }
  if (Object.prototype.hasOwnProperty.call(enumerationToLabel, t)) {
    return t;
  }
  const match = Object.entries(enumerationToLabel).find(([, label]) => label === t);
  if (match) {
    return match[0];
  }
  return t;
}
